import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getRepresentativeToken, removeRepresentativeToken, getMyProfile } from '../services/representativeService';
import { useRepresentativePresence } from '../hooks/useRepresentativePresence';

/**
 * Wrapper di gating per le route dell'area associativa (`/area-associativa/*`),
 * riservata ai rappresentanti associativi.
 *
 * Due controlli:
 *  1. Token: verifica `localStorage.representative_token` (presenza + scadenza).
 *     Se assente/scaduto → redirect a `/accesso-associati`.
 *  2. Permesso di accesso area: il rappresentante deve avere, su almeno una
 *     carica attiva, il permesso `associativa.dashboard.read`. Così l'admin può
 *     abilitare/disabilitare l'accesso all'intera area con un toggle sulla
 *     carica (senza toccare i singoli rappresentanti). Gate UI: i dati restano
 *     comunque protetti dai permessi per-funzione lato backend.
 *
 * Le due aree (associati vs associativa) restano separate perché si basano su
 * token/identità distinti. La sicurezza vera resta sul backend.
 */
const DASHBOARD_PERM = 'associativa.dashboard.read';

const isRepresentativeAuthenticated = (): boolean => {
  const token = getRepresentativeToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Date.now() / 1000;
    if (payload.exp && payload.exp < now) {
      removeRepresentativeToken();
      return false;
    }
    return true;
  } catch (error) {
    removeRepresentativeToken();
    return false;
  }
};

const RepresentativePrivateRoute: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const authed = isRepresentativeAuthenticated();
  // 'loading' | 'ok' | 'denied'
  const [access, setAccess] = useState<'loading' | 'ok' | 'denied'>('loading');
  // Conteggio "Collegati ora" nella dashboard admin: socket aperta finché si naviga nell'area
  useRepresentativePresence(authed && access === 'ok');

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    getMyProfile()
      .then((res: any) => {
        if (cancelled) return;
        const perms = res?.data?.permissions || [];
        setAccess(perms.some((p: any) => p.name === DASHBOARD_PERM) ? 'ok' : 'denied');
      })
      .catch((err: any) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401) { removeRepresentativeToken(); setAccess('denied'); }
        // Errore transitorio (rete/5xx): non bloccare l'utente (i dati restano gated lato backend)
        else if (status === 403) setAccess('denied');
        else setAccess('ok');
      });
    return () => { cancelled = true; };
  }, [authed]);

  if (!authed) {
    return (
      <Navigate
        to="/accesso-associati"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (access === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-emerald-600" />
      </div>
    );
  }

  if (access === 'denied') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-6">
        <div className="max-w-md w-full text-center bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-8">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">Accesso non consentito</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Le tue cariche non sono abilitate ad accedere all'Area Associativa.
            Per informazioni contatta la segreteria.
          </p>
          <button
            onClick={() => { removeRepresentativeToken(); navigate('/accesso-associati', { replace: true }); }}
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            Torna all'accesso
          </button>
        </div>
      </div>
    );
  }

  return <Outlet />;
};

export default RepresentativePrivateRoute;
